<template>
  <div class="auth-form-container">
    <!-- 装饰用柔光背景光晕 -->
    <div class="bg-glow-sphere glow-1"></div>
    <div class="bg-glow-sphere glow-2"></div>

    <div class="auth-card">
      <div class="auth-header">
        <!--        <div class="brand-logo-icon">-->
        <!--          <el-icon><Platform /></el-icon>-->
        <!--        </div>-->
        <h2 class="auth-title">
          {{ isLogin ? $t("login.title") : $t("register.title") }}
        </h2>
        <p class="auth-subtitle">BAGL Creator Studio · 创作者中枢</p>
      </div>

      <!-- 表单主体 -->
      <el-form
        ref="authFormRef"
        :model="authForm"
        :rules="formRules"
        label-position="top"
        @submit.prevent="handleSubmit"
        class="modern-form"
      >
        <el-form-item :label="$t('login.username')" prop="username">
          <el-input
            v-model="authForm.username"
            :placeholder="
              !isLogin ? $t('login.usernamePlaceholder') : '请输入用户名 / 账号'
            "
            size="large"
            clearable
            :prefix-icon="User"
          />
        </el-form-item>

        <el-form-item :label="$t('login.password')" prop="password">
          <el-input
            v-model="authForm.password"
            type="password"
            :placeholder="
              !isLogin ? $t('login.passwordPlaceholder') : '请输入密码'
            "
            show-password
            size="large"
            :prefix-icon="Lock"
          />
        </el-form-item>

        <!-- 注册特有字段 -->
        <template v-if="!isLogin">
          <el-form-item label="确认登录密码" prop="confirmPassword">
            <el-input
              v-model="authForm.confirmPassword"
              type="password"
              placeholder="请再次输入相同密码"
              show-password
              size="large"
              :prefix-icon="CircleCheck"
            />
          </el-form-item>

          <el-form-item label="绑定邮箱" prop="email">
            <div class="captcha-input-group">
              <el-input
                v-model="authForm.email"
                placeholder="请输入有效电子邮箱"
                size="large"
                :prefix-icon="Message"
              />
              <el-button
                type="primary"
                plain
                @click="sendCaptcha(1)"
                :disabled="isCountdown"
                class="captcha-btn"
                size="large"
              >
                {{ countdown > 0 ? `${countdown}s` : "获取验证码" }}
              </el-button>
            </div>
          </el-form-item>

          <el-form-item label="邮箱验证码" prop="captcha">
            <el-input
              v-model="authForm.captcha"
              placeholder="请输入 6 位验证码"
              size="large"
              maxlength="6"
              :prefix-icon="Key"
            />
          </el-form-item>
        </template>

        <div class="form-actions">
          <el-button
            type="primary"
            native-type="submit"
            size="large"
            :loading="submitting"
            class="submit-btn"
          >
            {{ isLogin ? $t("login.submit") : $t("register.submit") }}
          </el-button>
        </div>
      </el-form>

      <!-- 底部切换链接 -->
      <div class="auth-helper-links">
        <span @click="toggleAuthMode" class="helper-link">
          {{ isLogin ? $t("register.switch") : $t("login.switch") }}
        </span>
        <el-divider direction="vertical" />
        <span @click="showPage" class="helper-link text-muted">忘记密码?</span>
      </div>
    </div>
  </div>

  <!-- 忘记密码弹窗 -->
  <el-dialog
    v-model="centerDialogVisible"
    title="找回与重置密码"
    width="480px"
    destroy-on-close
    align-center
    class="forgot-password-dialog"
  >
    <el-form
      ref="forgotFormRef"
      :model="form"
      :rules="forgotRules"
      label-position="top"
      class="dialog-inner-form"
    >
      <el-form-item label="密保邮箱" prop="email">
        <el-input
          v-model="form.email"
          placeholder="请输入绑定的注册邮箱"
          size="large"
          :prefix-icon="Message"
        />
      </el-form-item>

      <el-form-item label="安全验证码" prop="code">
        <div class="captcha-input-group">
          <el-input
            v-model="form.code"
            placeholder="输入 6 位数字"
            size="large"
            :prefix-icon="Key"
          />
          <el-button
            type="primary"
            plain
            @click="sendCaptcha(2)"
            :disabled="isCountdown2"
            class="captcha-btn"
            size="large"
          >
            {{ countdown2 > 0 ? `${countdown2}s` : "获取验证码" }}
          </el-button>
        </div>
      </el-form-item>

      <el-form-item label="设置新密码" prop="password">
        <el-input
          v-model="form.password"
          type="password"
          show-password
          placeholder="输入新密码 (6 位以上字符)"
          size="large"
          :prefix-icon="Lock"
        />
      </el-form-item>

      <el-form-item label="确认新密码" prop="confirmPassword">
        <el-input
          v-model="form.confirmPassword"
          type="password"
          show-password
          placeholder="请再次确认新密码"
          size="large"
          :prefix-icon="CircleCheck"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <div class="dialog-footer">
        <el-button
          size="large"
          @click="centerDialogVisible = false"
          class="cancel-btn"
          >取消</el-button
        >
        <el-button
          type="primary"
          size="large"
          :loading="resetting"
          @click="editPassword"
          class="confirm-btn"
        >
          确认重置密码
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script lang="ts">
import { defineComponent, markRaw } from "vue";
import { ElMessage } from "element-plus";
import {
  User,
  Lock,
  Message,
  Key,
  CircleCheck,
  Platform,
} from "@element-plus/icons-vue";

export default defineComponent({
  name: "LoginRegister",
  data(): any {
    return {
      User: markRaw(User),
      Lock: markRaw(Lock),
      Message: markRaw(Message),
      Key: markRaw(Key),
      CircleCheck: markRaw(CircleCheck),
      Platform: markRaw(Platform),
      isLogin: true,
      submitting: false,
      resetting: false,
      authForm: {
        username: "",
        password: "",
        email: "",
        captcha: "",
        confirmPassword: "",
      },
      form: {
        code: "",
        password: "",
        confirmPassword: "",
        email: "",
      },
      countdown: 0,
      countdown2: 0,
      countdownInterval: null,
      countdownInterval2: null,
      centerDialogVisible: false,
    };
  },
  computed: {
    isCountdown(): boolean {
      return this.countdown > 0;
    },
    isCountdown2(): boolean {
      return this.countdown2 > 0;
    },
    formRules(): any {
      return {
        username: [
          { required: true, message: "请输入用户名", trigger: "blur" },
          {
            min: 3,
            max: 20,
            message: "用户名长度需在 3 到 20 位之间",
            trigger: "blur",
          },
        ],
        password: [
          { required: true, message: "请输入密码", trigger: "blur" },
          { min: 6, message: "密码长度不得少于 6 位", trigger: "blur" },
        ],
        confirmPassword: [{ validator: this.validatePass2, trigger: "blur" }],
        email: [
          { required: true, message: "请输入邮箱", trigger: "blur" },
          { type: "email", message: "邮箱格式不正确", trigger: "blur" },
        ],
        captcha: [
          { required: true, message: "请输入验证码", trigger: "blur" },
          { len: 6, message: "验证码应为 6 位", trigger: "blur" },
        ],
      };
    },
    forgotRules(): any {
      return {
        email: [
          { required: true, message: "请输入绑定邮箱", trigger: "blur" },
          { type: "email", message: "邮箱格式不正确", trigger: "blur" },
        ],
        code: [{ required: true, message: "请输入验证码", trigger: "blur" }],
        password: [
          { required: true, message: "请输入新密码", trigger: "blur" },
          { min: 6, message: "新密码长度不得少于 6 位", trigger: "blur" },
        ],
        confirmPassword: [
          { validator: this.validateForgotPass2, trigger: "blur" },
        ],
      };
    },
  },
  methods: {
    validatePass2(rule: any, value: string, callback: any) {
      if (!value) {
        callback(new Error("请再次输入密码"));
      } else if (value !== this.authForm.password) {
        callback(new Error("两次输入的密码不一致"));
      } else {
        callback();
      }
    },
    validateForgotPass2(rule: any, value: string, callback: any) {
      if (!value) {
        callback(new Error("请再次输入新密码"));
      } else if (value !== this.form.password) {
        callback(new Error("两次输入的密码不一致"));
      } else {
        callback();
      }
    },
    handleSubmit() {
      const formEl = this.$refs.authFormRef as any;
      if (!formEl) return;

      formEl.validate((valid: boolean) => {
        if (!valid) return;

        this.submitting = true;
        if (this.isLogin) {
          this.$store
            .dispatch("user/login", {
              username: this.authForm.username,
              password: this.authForm.password,
            })
            .then(() => {
              ElMessage.success("登录成功，正在进入平台...");
              this.$router.push("/");
            })
            .catch((err: any) => {
              console.error(err);
            })
            .finally(() => {
              this.submitting = false;
            });
        } else {
          this.$store
            .dispatch("user/register", this.authForm)
            .then(() => {
              ElMessage.success("注册成功，已自动登录");
              this.$router.push("/");
            })
            .catch((err: any) => {
              console.error(err);
            })
            .finally(() => {
              this.submitting = false;
            });
        }
      });
    },
    toggleAuthMode() {
      this.isLogin = !this.isLogin;
      this.authForm = {
        username: "",
        password: "",
        email: "",
        captcha: "",
        confirmPassword: "",
      };
      this.$nextTick(() => {
        const formEl = this.$refs.authFormRef as any;
        if (formEl) formEl.clearValidate();
      });
    },
    sendCaptcha(options: number) {
      const reg =
        /^([a-zA-Z0-9_-])+@([a-zA-Z0-9_-])+((\.[a-zA-Z0-9_-]{2,3}){1,2})$/;

      if (options === 1) {
        if (!this.authForm.email) {
          ElMessage.warning("请先输入邮箱地址");
          return;
        }
        if (!reg.test(this.authForm.email)) {
          ElMessage.warning("邮箱格式不正确");
          return;
        }

        this.$store
          .dispatch("user/GetEmailCode", {
            action: "注册账号",
            userName: this.authForm.email,
          })
          .then(() => {
            ElMessage.success("验证码已发送至邮箱，请注意查收");
          })
          .catch(() => {});

        this.countdown = 60;
        this.countdownInterval = setInterval(() => {
          if (this.countdown > 0) {
            this.countdown -= 1;
          } else {
            clearInterval(this.countdownInterval);
          }
        }, 1000);
      } else if (options === 2) {
        if (!this.form.email) {
          ElMessage.warning("请先输入绑定的邮箱");
          return;
        }
        if (!reg.test(this.form.email)) {
          ElMessage.warning("邮箱格式不正确");
          return;
        }

        this.$store
          .dispatch("user/GetEmailCode", {
            action: "忘记密码",
            userName: this.form.email,
          })
          .then(() => {
            ElMessage.success("验证码已发送至邮箱");
          })
          .catch(() => {});

        this.countdown2 = 60;
        this.countdownInterval2 = setInterval(() => {
          if (this.countdown2 > 0) {
            this.countdown2 -= 1;
          } else {
            clearInterval(this.countdownInterval2);
          }
        }, 1000);
      }
    },
    showPage() {
      this.form = {
        code: "",
        password: "",
        confirmPassword: "",
        email: "",
      };
      this.centerDialogVisible = true;
    },
    editPassword() {
      const formEl = this.$refs.forgotFormRef as any;
      if (!formEl) return;

      formEl.validate((valid: boolean) => {
        if (!valid) return;

        this.resetting = true;
        this.$store
          .dispatch("user/forgetPassword", {
            code: this.form.code,
            email: this.form.email,
            newPassword: this.form.confirmPassword,
          })
          .then(() => {
            ElMessage.success("密码重置成功，请使用新密码登录");
            this.centerDialogVisible = false;
          })
          .catch((err: any) => {
            console.error(err);
          })
          .finally(() => {
            this.resetting = false;
          });
      });
    },
  },
  beforeUnmount() {
    if (this.countdownInterval) clearInterval(this.countdownInterval);
    if (this.countdownInterval2) clearInterval(this.countdownInterval2);
  },
});
</script>

<style scoped lang="less">
@primary-color: #5d5fef;
@primary-hover: #4b4de0;
@primary-light: #f0f2ff;
@text-main: #0f172a;
@text-sub: #475569;
@text-muted: #94a3b8;
@border-color: #e2e8f0;
@card-border: rgba(255, 255, 255, 0.7);

/* 容器及背景环境光 */
.auth-form-container {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  width: 100%;
  background: radial-gradient(circle at 10% 20%, #f8fafc 0%, #edf2f7 90%);
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  padding: 24px;

  .bg-glow-sphere {
    position: absolute;
    border-radius: 50%;
    filter: blur(90px);
    pointer-events: none;
    z-index: 0;

    &.glow-1 {
      width: 420px;
      height: 420px;
      background: rgba(93, 95, 239, 0.12);
      top: -80px;
      right: -60px;
    }

    &.glow-2 {
      width: 380px;
      height: 380px;
      background: rgba(14, 165, 233, 0.1);
      bottom: -60px;
      left: -60px;
    }
  }
}

/* 磨砂玻璃轻奢卡片 */
.auth-card {
  width: 100%;
  max-width: 440px;
  padding: 40px 36px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.88);
  backdrop-filter: blur(16px);
  box-shadow: 0 20px 48px -12px rgba(15, 23, 42, 0.1),
    0 1px 2px rgba(15, 23, 42, 0.04);
  border: 1px solid @card-border;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
  transition: all 0.3s ease;

  .auth-header {
    text-align: center;
    margin-bottom: 28px;

    .brand-logo-icon {
      width: 52px;
      height: 52px;
      border-radius: 14px;
      background: linear-gradient(135deg, @primary-color 0%, #818cf8 100%);
      color: #ffffff;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      font-size: 26px;
      box-shadow: 0 8px 18px rgba(93, 95, 239, 0.3);
      margin-bottom: 14px;
    }

    .auth-title {
      margin: 0 0 6px 0;
      font-size: 22px;
      font-weight: 800;
      color: @text-main;
      letter-spacing: -0.3px;
    }

    .auth-subtitle {
      margin: 0;
      font-size: 13px;
      color: @text-muted;
      font-weight: 500;
    }
  }
}

/* 现代化表单排版 */
.modern-form {
  :deep(.el-form-item) {
    margin-bottom: 20px;

    .el-form-item__label {
      font-size: 13px;
      font-weight: 600;
      color: @text-sub;
      padding-bottom: 6px;
      line-height: 1;
    }

    .el-input__wrapper {
      border-radius: 10px;
      background: #f8fafc;
      box-shadow: 0 0 0 1px @border-color inset;
      padding: 4px 14px;
      transition: all 0.25s ease;

      &:hover {
        box-shadow: 0 0 0 1px #cbd5e1 inset;
        background: #ffffff;
      }

      &.is-focus {
        background: #ffffff;
        box-shadow: 0 0 0 2px @primary-color inset !important;
      }
    }

    .el-form-item__error {
      padding-top: 4px;
      font-size: 12px;
    }
  }
}

/* 验证码弹性组合输入框 */
.captcha-input-group {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;

  .el-input {
    flex: 1;
  }

  .captcha-btn {
    min-width: 112px;
    height: 40px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    color: @primary-color;
    border-color: #dce2ff;
    background: @primary-light;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: @primary-color;
      border-color: @primary-color;
      color: #ffffff;
    }

    &:disabled {
      background: #f1f5f9;
      border-color: @border-color;
      color: @text-muted;
    }
  }
}

/* 核心提交大按钮 */
.form-actions {
  margin-top: 26px;

  .submit-btn {
    width: 100%;
    height: 44px;
    font-size: 14.5px;
    font-weight: 700;
    border-radius: 10px;
    background: @primary-color;
    border-color: @primary-color;
    box-shadow: 0 4px 14px rgba(93, 95, 239, 0.28);
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);

    &:hover {
      background: @primary-hover;
      border-color: @primary-hover;
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(93, 95, 239, 0.38);
    }

    &:active {
      transform: translateY(0);
    }
  }
}

/* 辅助操作链接 */
.auth-helper-links {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 22px;
  font-size: 13px;

  .helper-link {
    cursor: pointer;
    color: @primary-color;
    font-weight: 600;
    transition: all 0.2s;
    user-select: none;

    &:hover {
      color: @primary-hover;
      text-decoration: underline;
    }

    &.text-muted {
      color: @text-muted;
      font-weight: 500;

      &:hover {
        color: @text-sub;
        text-decoration: none;
      }
    }
  }

  .el-divider {
    border-color: @border-color;
    margin: 0 12px;
  }
}

/* 忘记密码弹窗 */
:deep(.forgot-password-dialog) {
  border-radius: 18px;
  overflow: hidden;

  .el-dialog__header {
    margin: 0;
    padding: 20px 24px;
    border-bottom: 1px solid @border-color;

    .el-dialog__title {
      font-size: 16px;
      font-weight: 700;
      color: @text-main;
    }
  }

  .el-dialog__body {
    padding: 24px;

    .dialog-inner-form {
      .el-form-item {
        margin-bottom: 18px;

        .el-form-item__label {
          font-size: 13px;
          font-weight: 600;
          color: @text-sub;
          padding-bottom: 4px;
        }

        .el-input__wrapper {
          border-radius: 8px;
        }
      }
    }
  }

  .el-dialog__footer {
    padding: 14px 24px 20px;
    border-top: 1px solid @border-color;

    .dialog-footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;

      .cancel-btn {
        border-radius: 8px;
      }

      .confirm-btn {
        border-radius: 8px;
        background: @primary-color;
        border-color: @primary-color;
        font-weight: 600;

        &:hover {
          background: @primary-hover;
          border-color: @primary-hover;
        }
      }
    }
  }
}
</style>
